import { useEffect, useMemo, useState } from "react";
import {
  FileBarChart, RefreshCw, Download, FileSpreadsheet, Printer, ArrowRightLeft,
  Boxes, Droplets, Calculator, Mail, Send, Loader2, Save,
} from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { downloadCsv, downloadXlsx, downloadReportPdf } from "../lib/exporters";

const fmtDateInput = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();
const daysAgo = (n) => new Date(Date.now() - n * 86400000);
const fmtDateOnly = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB") : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-GB") : "—";
const fmtMoney = (n) => `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TABS = [
  { key: "gate", label: "Gate Events", icon: ArrowRightLeft, testid: "tab-report-gate" },
  { key: "stock", label: "Stock Snapshot", icon: Boxes, testid: "tab-report-stock" },
  { key: "wash", label: "Wash Activity", icon: Droplets, testid: "tab-report-wash" },
  { key: "lolo", label: "Lift Activity", icon: Calculator, testid: "tab-report-lolo" },
];

function DailyDepotReportCard() {
  const [cfg, setCfg] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/depot-report/config")
      .then(({ data }) => {
        setCfg(data);
        setRecipientsText((data.recipients || []).join(", "));
        setEnabled(!!data.enabled);
      })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const recipients = recipientsText.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) { toast.error("Add at least one email address"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/depot-report/config", { recipients, enabled });
      setCfg(data);
      toast.success("Daily depot report settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/depot-report/send-now");
      if (data.status === "SENT") {
        toast.success(`Report sent to ${data.recipients.join(", ")} — ${data.in_depot} containers in depot`);
      } else {
        toast.warning(`Not sent — ${data.error || data.reason || data.status}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (!cfg) return null;

  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-4" data-testid="daily-depot-report-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-900">
            <Mail className="w-3.5 h-3.5" /> Daily depot report — internal
          </div>
          <div className="text-xs text-gray-600 mt-1">
            Every day at 08:00 GMT: one email with all carriers rolled up (in-depot, food/chem split, last-24h gate moves)
            plus a detailed Excel attachment.
            {cfg.last_sent_at && (
              <span className="ml-2 text-gray-500" data-testid="depot-report-last-sent">
                Last sent: {fmtDateTime(cfg.last_sent_at)}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <a
            className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
            href={`${api.defaults.baseURL}/depot-report/preview?download=1`}
            target="_blank" rel="noreferrer"
            data-testid="depot-report-download"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Preview Excel
          </a>
          <button className="ps-btn ps-btn-primary h-8 px-3 text-xs" onClick={sendNow} disabled={sending} data-testid="depot-report-send-now">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[280px]">
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Recipients (comma-separated)</label>
          <input
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="depot-tema@portside.ch, ops@portside.ch"
            className="ps-input font-mono text-sm"
            data-testid="depot-report-recipients"
          />
        </div>
        <label className="inline-flex items-center gap-2 h-9 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4" data-testid="depot-report-enabled" />
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-gray-700">Enabled</span>
        </label>
        <button className="ps-btn ps-btn-secondary h-9" onClick={save} disabled={saving} data-testid="depot-report-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>
    </div>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("gate");
  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports"
        subtitle="Generate, preview, and export depot operations reports (PDF + Excel + CSV)."
        testid="reports-header"
      />
      <div className="px-6 sm:px-8 pt-5">
        <DailyDepotReportCard />
      </div>
      <div className="px-6 sm:px-8 pt-5">
        <a
          href="/reports/long-stay-hlc"
          className="block border border-amber-200 bg-amber-50/60 hover:bg-amber-50 rounded-sm p-4"
          data-testid="long-stay-shortcut"
        >
          <div className="flex items-start gap-3">
            <FileBarChart className="w-5 h-5 text-amber-900 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-900">Ad-hoc reconciliation</div>
              <div className="text-sm font-bold text-gray-900 mt-0.5">HLC long-stay report</div>
              <div className="text-xs text-gray-600 mt-1">
                Rent-billing worksheet for HLC — every container still on-yard, with days billable from a chosen cut-off date. Open →
              </div>
            </div>
          </div>
        </a>
      </div>
      <div className="px-6 sm:px-8 pt-5">
        <div className="flex flex-wrap gap-1.5 border-b border-gray-200" data-testid="reports-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={t.testid}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 h-9 text-sm font-medium rounded-t-sm -mb-px border-b-2 transition-colors ${
                  active
                    ? "border-[#006E9E] text-[#006E9E] bg-blue-50/50"
                    : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-6 sm:p-8">
        {tab === "gate" && <GateEventsReport />}
        {tab === "stock" && <StockReport />}
        {tab === "wash" && <WashReport />}
        {tab === "lolo" && <LoloReport />}
      </div>
    </div>
  );
}

/* ---------- shared filter row ---------- */
function FilterCard({ children }) {
  return (
    <div className="border border-gray-200 rounded-sm bg-gray-50 p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="uppercase tracking-wider text-gray-500 text-[10px]">{label}</span>
      {children}
    </label>
  );
}

/* ============ 1. GATE EVENTS ============ */
function GateEventsReport() {
  const [from, setFrom] = useState(fmtDateInput(daysAgo(30)));
  const [to, setTo] = useState(fmtDateInput(today()));
  const [gateType, setGateType] = useState("");
  const [carrier, setCarrier] = useState("");
  const [search, setSearch] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    api.get("/containers/carriers").then(({ data }) => setCarriers(data || [])).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: new Date(from).toISOString(),
        date_to: new Date(to + "T23:59:59").toISOString(),
        limit: "500",
      });
      if (gateType) params.set("gate_type", gateType);
      if (carrier) params.set("carrier_code", carrier);
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/gate-events?${params.toString()}`);
      setRows(data || []);
      setHasRun(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // Live filter — narrows the already-loaded rows without re-hitting the API.
  // Handy when the operator has run a broad date range and wants to poke around
  // by booking / container / truck without re-generating.
  const displayedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term || !hasRun) return rows;
    return rows.filter((r) => {
      const hay = [r.container_number, r.booking_number, r.truck_plate, r.customer, r.driver_name]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [rows, search, hasRun]);

  const kpis = useMemo(() => {
    const inN = displayedRows.filter((r) => r.gate_type === "IN").length;
    const outN = displayedRows.filter((r) => r.gate_type === "OUT").length;
    const carriersSet = new Set(displayedRows.map((r) => r.carrier_code || r.last_carrier).filter(Boolean));
    return { total: displayedRows.length, gate_in: inN, gate_out: outN, carriers: carriersSet.size };
  }, [displayedRows]);

  const columns = [
    { header: "Event Time", key: "event_time", format: fmtDateTime, width: 90 },
    { header: "Container", key: "container_number", mono: true, width: 80 },
    { header: "ISO", key: "container_iso_type", width: 40 },
    { header: "Gate", key: "gate_type", width: 30 },
    { header: "Carrier", key: "carrier", format: (_, r) => r.carrier_code || r.last_carrier || "—", width: 60 },
    { header: "Customer", key: "customer", format: (v) => v || "—" },
    { header: "Booking #", key: "booking_number", mono: true, format: (v) => v || "—", width: 90 },
    { header: "Truck", key: "truck_plate", format: (v) => v || "—", width: 70 },
    { header: "Driver", key: "driver_name", format: (v) => v || "—", width: 80 },
    { header: "Status", key: "status", width: 60 },
  ];

  const filename = `gate_events_${from}_${to}`;
  const filters = [
    { label: "Period", value: `${from} → ${to}` },
    { label: "Carrier", value: carrier || "All" },
    { label: "Gate type", value: gateType || "All" },
    ...(search.trim() ? [{ label: "Search", value: search.trim() }] : []),
  ];
  const totals = [
    { label: "Total events", value: kpis.total, bold: true },
    { label: "Gate IN", value: kpis.gate_in },
    { label: "Gate OUT", value: kpis.gate_out },
    { label: "Distinct carriers", value: kpis.carriers },
  ];

  return (
    <div className="space-y-4" data-testid="gate-events-report">
      <FilterCard>
        <Field label="From"><input data-testid="gate-rep-from" type="date" className="ps-input h-8" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input data-testid="gate-rep-to" type="date" className="ps-input h-8" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Gate type">
          <select data-testid="gate-rep-type" className="ps-input h-8" value={gateType} onChange={(e) => setGateType(e.target.value)}>
            <option value="">All</option><option value="IN">Gate IN</option><option value="OUT">Gate OUT</option>
          </select>
        </Field>
        <Field label="Carrier">
          <select data-testid="gate-rep-carrier" className="ps-input h-8" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
            <option value="">All</option>
            {carriers.map((c) => <option key={c.carrier} value={c.carrier}>{c.carrier} ({c.count})</option>)}
          </select>
        </Field>
        <Field label="Search">
          <input
            data-testid="gate-rep-search"
            type="text"
            className="ps-input h-8 font-mono"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && generate()}
            placeholder="Booking #, container, truck, driver…"
            style={{ minWidth: 220 }}
          />
        </Field>
        <div className="ml-auto flex gap-2">
          <button data-testid="gate-rep-generate" className="ps-btn ps-btn-primary" disabled={loading} onClick={generate}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileBarChart className="w-4 h-4" />} Generate
          </button>
        </div>
      </FilterCard>

      {hasRun && (
        <>
          <KpiStrip
            kpis={[
              { label: "Total events", value: kpis.total },
              { label: "Gate IN", value: kpis.gate_in, color: "#16a34a" },
              { label: "Gate OUT", value: kpis.gate_out, color: "#d97706" },
              { label: "Distinct carriers", value: kpis.carriers },
            ]}
          />
          <ExportRow
            csvFn={() => downloadCsv(displayedRows, filename, columns.map((c) => ({ key: c.key, label: c.header, format: c.format })))}
            xlsxFn={() => downloadXlsx(displayedRows, filename, columns.map((c) => ({ key: c.key, label: c.header, format: c.format })), "Gate Events")}
            pdfFn={() => downloadReportPdf({ title: "Gate Events Report", filename, filters, columns, rows: displayedRows, totals, orientation: "landscape" })}
            disabled={displayedRows.length === 0}
            testidPrefix="gate-rep"
          />
          <DataTable columns={columns} rows={displayedRows} testid="gate-rep-table" emptyText={search.trim() ? `No gate events match search "${search.trim()}" within these filters.` : "No gate events match these filters."} />
        </>
      )}
    </div>
  );
}

/* ============ 2. STOCK ============ */
function StockReport() {
  const [carrier, setCarrier] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock/summary");
      setSummary(data);
      setHasRun(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    if (!summary?.breakdown) return [];
    const f = carrier ? summary.breakdown.filter((r) => r.carrier === carrier) : summary.breakdown;
    return f.map((r) => ({ ...r, sample_text: (r.sample || []).join(", ") }));
  }, [summary, carrier]);

  const allCarriers = summary?.by_carrier || [];
  const filteredTotal = rows.reduce((s, r) => s + (r.count || 0), 0);

  const columns = [
    { header: "Carrier", key: "carrier", width: 70 },
    { header: "ISO Type", key: "iso_type", width: 70 },
    { header: "Grade", key: "grade", width: 50 },
    { header: "Condition", key: "condition", width: 80 },
    { header: "Units", key: "count", align: "right", width: 50 },
    { header: "Sample (first 5)", key: "sample_text", mono: true },
  ];

  const filename = `stock_snapshot_${fmtDateInput(today())}${carrier ? "_" + carrier : ""}`;
  const filters = [
    { label: "Snapshot", value: new Date().toLocaleDateString("en-GB") },
    { label: "Carrier", value: carrier || "All" },
  ];
  const totals = [
    { label: "Containers in depot", value: filteredTotal, bold: true },
    { label: "Total in depot (all carriers)", value: summary?.total_in_depot || 0 },
    { label: "Carrier breakdown rows", value: rows.length },
  ];

  return (
    <div className="space-y-4" data-testid="stock-report">
      <FilterCard>
        <Field label="Carrier">
          <select data-testid="stock-rep-carrier" className="ps-input h-8" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
            <option value="">All carriers</option>
            {allCarriers.map((c) => <option key={c.carrier} value={c.carrier}>{c.carrier} ({c.count})</option>)}
          </select>
        </Field>
        <div className="ml-auto flex gap-2">
          <button data-testid="stock-rep-generate" className="ps-btn ps-btn-secondary" disabled={loading} onClick={generate}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </FilterCard>

      {hasRun && summary && (
        <>
          <KpiStrip
            kpis={[
              { label: "In depot (filtered)", value: filteredTotal },
              { label: "Total in depot", value: summary.total_in_depot },
              { label: "Distinct carriers", value: allCarriers.length },
              { label: "Breakdown rows", value: rows.length },
            ]}
          />
          <ExportRow
            csvFn={() => downloadCsv(rows, filename, columns.map((c) => ({ key: c.key, label: c.header })))}
            xlsxFn={() => downloadXlsx(rows, filename, columns.map((c) => ({ key: c.key, label: c.header })), "Stock Snapshot")}
            pdfFn={() => downloadReportPdf({ title: "Stock Snapshot Report", filename, filters, columns, rows, totals })}
            disabled={rows.length === 0}
            testidPrefix="stock-rep"
          />
          <DataTable columns={columns} rows={rows} testid="stock-rep-table" emptyText="No stock for this carrier." />
        </>
      )}
    </div>
  );
}

/* ============ 3. WASH ============ */
function WashReport() {
  const [from, setFrom] = useState(fmtDateInput(daysAgo(90)));
  const [to, setTo] = useState(fmtDateInput(today()));
  const [status, setStatus] = useState("");
  const [customer, setCustomer] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    api.get("/wash/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: new Date(from).toISOString(),
        date_to: new Date(to + "T23:59:59").toISOString(),
        limit: "2000",
      });
      if (status) params.set("status", status);
      if (customer) params.set("customer", customer);
      const { data } = await api.get(`/wash-orders?${params.toString()}`);
      setRows(data || []);
      setHasRun(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const kpis = useMemo(() => {
    const c = (s) => rows.filter((r) => r.status === s).length;
    const food = rows.filter((r) => r.wash_category === "FOOD" && ["CERTIFIED", "HISTORICAL"].includes(r.status));
    const chem = rows.filter((r) => r.wash_category === "CHEMICAL" && ["CERTIFIED", "HISTORICAL"].includes(r.status));
    const billed = rows.filter((r) => ["CERTIFIED", "HISTORICAL"].includes(r.status))
      .reduce((s, r) => s + Number(r.unit_price || 0), 0);
    return {
      total: rows.length,
      certified: c("CERTIFIED") + c("HISTORICAL"),
      in_progress: c("REQUESTED") + c("IN_PROGRESS") + c("COMPLETED"),
      cancelled: c("CANCELLED"),
      food: food.length,
      chemical: chem.length,
      billed,
    };
  }, [rows]);

  const columns = [
    { header: "Wash No", key: "wash_no", mono: true, width: 95 },
    { header: "Tank", key: "tank_number", mono: true, width: 75 },
    { header: "Category", key: "wash_category", format: (v) => v || "—", width: 55 },
    { header: "Price", key: "unit_price", format: (v, r) => v ? `${r.currency || "USD"} ${fmtMoney(v)}` : "—", align: "right", width: 70 },
    { header: "Customer", key: "customer", format: (v) => v || "—" },
    { header: "Cleaned for", key: "cleaned_for", format: (v) => v || "—" },
    { header: "Status", key: "status", width: 55 },
    { header: "Cert No", key: "cleaning_cert_no", mono: true, format: (v) => v || "—", width: 95 },
    { header: "Created", key: "created_at", format: fmtDateOnly, width: 70 },
  ];

  const filename = `wash_activity_${from}_${to}`;
  const filters = [
    { label: "Period", value: `${from} → ${to}` },
    { label: "Status", value: status || "All" },
    { label: "Customer", value: customer || "All" },
    summary?.pricing && { label: "Rates", value: (summary.pricing.types || []).map((t) => `${t.code} ${summary.pricing.currency} ${fmtMoney(t.price)}`).join("  ·  ") },
  ].filter(Boolean);
  const totals = [
    { label: "Total wash orders", value: kpis.total, bold: true },
    { label: "Food washes (billed)", value: kpis.food },
    { label: "Chemical washes (billed)", value: kpis.chemical },
    { label: "Billed revenue", value: `USD ${fmtMoney(kpis.billed)}`, bold: true },
  ];

  return (
    <div className="space-y-4" data-testid="wash-report">
      <FilterCard>
        <Field label="From"><input data-testid="wash-rep-from" type="date" className="ps-input h-8" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input data-testid="wash-rep-to" type="date" className="ps-input h-8" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Status">
          <select data-testid="wash-rep-status" className="ps-input h-8" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {["REQUESTED", "IN_PROGRESS", "COMPLETED", "CERTIFIED", "HISTORICAL", "CANCELLED"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Customer">
          <input data-testid="wash-rep-customer" className="ps-input h-8" placeholder="Search customer…" value={customer} onChange={(e) => setCustomer(e.target.value)} />
        </Field>
        <div className="ml-auto flex gap-2">
          <button data-testid="wash-rep-generate" className="ps-btn ps-btn-primary" disabled={loading} onClick={generate}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileBarChart className="w-4 h-4" />} Generate
          </button>
        </div>
      </FilterCard>

      {summary?.pricing && (
        <div className="text-xs text-gray-600 -mt-2 px-1" data-testid="wash-rep-rates-hint">
          Wash pricing: {(summary.pricing.types || []).map((t, i) => (
            <span key={t.code}>
              {i > 0 && " · "}
              <span className="font-semibold text-gray-800">{t.label} {summary.pricing.currency} {fmtMoney(t.price)}</span>
            </span>
          ))}
          {" · "}billed on CERTIFIED + HISTORICAL washes only.
        </div>
      )}

      {hasRun && (
        <>
          <KpiStrip
            kpis={[
              { label: "Total in window", value: kpis.total },
              { label: "Food (billed)", value: kpis.food, color: "#16a34a" },
              { label: "Chemical (billed)", value: kpis.chemical, color: "#d97706" },
              { label: "Revenue (USD)", value: fmtMoney(kpis.billed), color: "#006E9E" },
            ]}
          />
          <ExportRow
            csvFn={() => downloadCsv(rows, filename, columns.map((c) => ({ key: c.key, label: c.header, format: c.format })))}
            xlsxFn={() => downloadXlsx(rows, filename, columns.map((c) => ({ key: c.key, label: c.header, format: c.format })), "Wash Activity")}
            pdfFn={() => downloadReportPdf({ title: "Wash Activity Report", filename, filters, columns, rows, totals, orientation: "landscape" })}
            disabled={rows.length === 0}
            testidPrefix="wash-rep"
          />
          <DataTable columns={columns} rows={rows} testid="wash-rep-table" emptyText="No wash orders match these filters." />
        </>
      )}
    </div>
  );
}

/* ============ 4. LOLO ============ */
function LoloReport() {
  const [from, setFrom] = useState(fmtDateInput(daysAgo(30)));
  const [to, setTo] = useState(fmtDateInput(today()));
  const [group, setGroup] = useState("carrier");
  const [bucket, setBucket] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: new Date(from).toISOString(),
        date_to: new Date(to + "T23:59:59").toISOString(),
        group, bucket,
      });
      const { data } = await api.get(`/billing/lolo?${params.toString()}`);
      setData(data);
      setHasRun(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const parties = data?.parties || [];
  const totals = data?.totals || {};
  const rows = parties.map((p) => ({
    party: p.party,
    total_lolo: p.total_lolo,
    total_in: p.total_in,
    total_out: p.total_out,
    periods: (p.periods || []).length,
  }));
  const columns = [
    { header: group === "carrier" ? "Carrier" : "Customer", key: "party" },
    { header: "Total LOLO", key: "total_lolo", align: "right", width: 70 },
    { header: "Gate IN", key: "total_in", align: "right", width: 60 },
    { header: "Gate OUT", key: "total_out", align: "right", width: 60 },
    { header: "Periods", key: "periods", align: "right", width: 55 },
  ];

  const filename = `lolo_${group}_${from}_${to}`;
  const filters = [
    { label: "Period", value: `${from} → ${to}` },
    { label: "Group by", value: group },
    { label: "Bucket", value: bucket },
  ];
  const pdfTotals = [
    { label: "Total LOLO", value: totals.total_lolo || 0, bold: true },
    { label: "Total Gate IN", value: totals.total_in || 0 },
    { label: "Total Gate OUT", value: totals.total_out || 0 },
    { label: "Unique parties", value: totals.unique_parties || 0 },
  ];

  return (
    <div className="space-y-4" data-testid="lolo-report">
      <FilterCard>
        <Field label="From"><input data-testid="lolo-rep-from" type="date" className="ps-input h-8" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input data-testid="lolo-rep-to" type="date" className="ps-input h-8" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Group by">
          <select data-testid="lolo-rep-group" className="ps-input h-8" value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="carrier">Carrier</option><option value="customer">Customer</option>
          </select>
        </Field>
        <Field label="Bucket">
          <select data-testid="lolo-rep-bucket" className="ps-input h-8" value={bucket} onChange={(e) => setBucket(e.target.value)}>
            <option value="day">Day</option><option value="week">Week</option><option value="month">Month</option>
          </select>
        </Field>
        <div className="ml-auto flex gap-2">
          <button data-testid="lolo-rep-generate" className="ps-btn ps-btn-primary" disabled={loading} onClick={generate}>
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileBarChart className="w-4 h-4" />} Generate
          </button>
        </div>
      </FilterCard>

      {hasRun && data && (
        <>
          <KpiStrip
            kpis={[
              { label: "Total LOLO", value: totals.total_lolo || 0 },
              { label: "Gate IN", value: totals.total_in || 0, color: "#16a34a" },
              { label: "Gate OUT", value: totals.total_out || 0, color: "#d97706" },
              { label: "Unique " + (group === "carrier" ? "carriers" : "customers"), value: totals.unique_parties || 0 },
            ]}
          />
          <ExportRow
            csvFn={() => downloadCsv(rows, filename, columns.map((c) => ({ key: c.key, label: c.header })))}
            xlsxFn={() => downloadXlsx(rows, filename, columns.map((c) => ({ key: c.key, label: c.header })), "Lift Activity")}
            pdfFn={() => downloadReportPdf({ title: "Lift Activity Report", filename, filters, columns, rows, totals: pdfTotals })}
            disabled={rows.length === 0}
            testidPrefix="lolo-rep"
          />
          <DataTable columns={columns} rows={rows} testid="lolo-rep-table" emptyText="No LOLO activity in this window." />
        </>
      )}
    </div>
  );
}

/* ============ shared bits ============ */
function KpiStrip({ kpis }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-kpis">
      {kpis.map((k) => (
        <div key={k.label} className="border border-gray-200 bg-white rounded-sm px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">{k.label}</div>
          <div className="text-2xl font-display font-extrabold tabular-nums mt-0.5" style={k.color ? { color: k.color } : {}}>
            {typeof k.value === "number" ? k.value.toLocaleString() : k.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function ExportRow({ csvFn, xlsxFn, pdfFn, disabled, testidPrefix }) {
  return (
    <div className="flex flex-wrap justify-end gap-2" data-testid={`${testidPrefix}-export-row`}>
      <button data-testid={`${testidPrefix}-csv`} className="ps-btn ps-btn-secondary" disabled={disabled} onClick={csvFn}>
        <Download className="w-4 h-4" /> CSV
      </button>
      <button data-testid={`${testidPrefix}-xlsx`} className="ps-btn ps-btn-secondary" disabled={disabled} onClick={xlsxFn}>
        <FileSpreadsheet className="w-4 h-4" /> XLSX
      </button>
      <button data-testid={`${testidPrefix}-pdf`} className="ps-btn ps-btn-primary" disabled={disabled} onClick={pdfFn}>
        <Printer className="w-4 h-4" /> PDF
      </button>
    </div>
  );
}

function DataTable({ columns, rows, testid, emptyText }) {
  if (!rows.length) {
    return <div className="border border-dashed border-gray-300 bg-gray-50 rounded-sm py-10 text-center text-sm text-gray-500" data-testid={`${testid}-empty`}>{emptyText}</div>;
  }
  return (
    <div className="border border-gray-200 rounded-sm overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
          <tr>{columns.map((c) => <th key={c.header} className={`px-3 py-2 ${c.align === "right" ? "text-right" : "text-left"} font-semibold`}>{c.header}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.slice(0, 200).map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {columns.map((c) => {
                const v = c.format ? c.format(r[c.key], r) : r[c.key];
                return <td key={c.header} className={`px-3 py-2 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.mono ? "font-mono" : ""}`}>{v == null || v === "" ? "—" : String(v)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 200 && (
        <div className="text-[11px] text-gray-500 px-3 py-2 border-t border-gray-100 bg-gray-50">
          Showing first 200 of {rows.length.toLocaleString()} rows · Full set included in PDF / XLSX / CSV exports.
        </div>
      )}
    </div>
  );
}
